/**
 * Suggestie voor de TimeChimp-koppeling: welk account hoort waarschijnlijk bij
 * deze medewerker? Alfabetisch zoeken in een lijst van tientallen accounts is
 * geen doen, en de reden dat iemand niet automatisch gekoppeld is, is nou juist
 * dat het e-mailadres afwijkt. Vergelijken op naam is dan het enige dat rest.
 *
 * Bewust een simpele token-vergelijking en geen fuzzy-bibliotheek: het gaat om
 * Nederlandse namen die identiek gespeld zijn maar anders geformatteerd
 * ("Jansen, Piet" vs "Piet Jansen", "p.jansen@" vs "piet.jansen@").
 */

/** Tussenvoegsels en titels dragen geen onderscheidend vermogen. */
const RUIS = new Set(['van', 'de', 'der', 'den', 'ten', 'ter', 'het', 'du', 'la', 'dr', 'drs']);

function tokens(waarde: string): string[] {
  return waarde
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // diakrieten weg: Sévérine == Severine
    .split(/[^a-z]+/)
    .filter((t) => t.length >= 2 && !RUIS.has(t));
}

export interface KoppelAccount {
  id: number | string;
  email: string;
  displayName: string;
}

/**
 * Hoe sterk lijkt dit account op deze medewerker? 0 = geen enkel raakvlak.
 * Een gedeeld naamdeel telt zwaarder dan een gedeeld stukje e-mailadres, want
 * het e-mailadres is precies wat hier uit de pas loopt.
 */
export function naamScore(
  medewerker: { displayName: string; email: string },
  account: KoppelAccount,
): number {
  const mens = new Set([
    ...tokens(medewerker.displayName),
    ...tokens(medewerker.email.split('@')[0] ?? ''),
  ]);
  if (mens.size === 0) return 0;
  const naamDelen = new Set(tokens(account.displayName));
  const mailDelen = new Set(tokens(account.email.split('@')[0] ?? ''));
  let score = 0;
  for (const deel of mens) {
    if (naamDelen.has(deel)) score += 2;
    else if (mailDelen.has(deel)) score += 1;
    // Initiaal tegen voornaam: "p.jansen" bij "Piet Jansen".
    else if (
      deel.length > 3 &&
      [...mailDelen].some((m) => m.length > 3 && m.startsWith(deel.slice(0, 4)))
    )
      score += 1;
  }
  return score;
}

/**
 * Splits de accounts in een suggestiekopje en de rest. Hooguit drie suggesties:
 * meer is geen suggestie meer maar een tweede lijst. Al gekoppelde accounts
 * blijven staan (de server weigert ze) maar zakken naar beneden, zodat de
 * bovenste suggestie er een is die je ook echt kunt kiezen.
 */
export function suggesties<T extends KoppelAccount & { gekoppeldAan?: string | null }>(
  medewerker: { displayName: string; email: string },
  accounts: T[],
  // Minimale score. Standaard 1 (TimeChimp: volledige namen aan beide kanten).
  // Intervisie gebruikt 4: daar staat aan één kant alleen het stukje vóór de @,
  // en dan is één gedeelde voornaam ("anne") geen voorstel maar ruis.
  minScore = 1,
): { voorgesteld: T[]; overig: T[] } {
  const gescoord = accounts
    .map((a) => ({ a, score: naamScore(medewerker, a) - (a.gekoppeldAan ? 1 : 0) }))
    .filter((x) => x.score >= minScore)
    .sort((x, y) => y.score - x.score);
  const voorgesteld = gescoord.slice(0, 3).map((x) => x.a);
  const gekozen = new Set(voorgesteld.map((a) => a.id));
  return { voorgesteld, overig: accounts.filter((a) => !gekozen.has(a.id)) };
}
